import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import PageLoader from "./components/PageLoader";
import api from "./api";

// ─── Types ────────────────────────────────────────────────────────────────────

const QUEST_KINDS = [
  "DAILY_TRADE_ASSET",
  "DAILY_TRADE_VOLUME",
  "DAILY_TRADE_COUNT",
  "DAILY_TRADE_BOTH_SIDES",
  "DAILY_TRADE_PROFIT",
  "DAILY_TRADE_DIVERSITY",
  "DAILY_TRADE_HIGH_STAKES",
  "DAILY_TRADE_SPEED",
  "DAILY_COMMENT_TWITTER",
] as const;

type QuestKind = (typeof QUEST_KINDS)[number];

interface QuestParams {
  asset?: string;
  minVolumeUsd?: number;
  tradeCount?: number;
  windowMinutes?: number;
  minSingleTradeUsd?: number;
  diversityCount?: number;
  tweetUrl?: string;
}

interface DailyTemplate {
  templateId: string;
  kind: QuestKind;
  titleTemplate: string;
  descriptionTemplate: string;
  rewardXp: number;
  rewardTickets: number;
  weight: number;
  enabled: boolean;
  params: QuestParams;
}

interface DailyConfig {
  activePerDay: number;
  enabled: boolean;
}

// ─── Blank template for "create" mode ─────────────────────────────────────────

const BLANK_TEMPLATE: DailyTemplate = {
  templateId: "",
  kind: "DAILY_TRADE_COUNT",
  titleTemplate: "",
  descriptionTemplate: "",
  rewardXp: 100,
  rewardTickets: 1,
  weight: 5,
  enabled: true,
  params: {},
};

// ─── Param fields by kind ─────────────────────────────────────────────────────

function ParamFields({
  kind,
  params,
  onChange,
}: {
  kind: QuestKind;
  params: QuestParams;
  onChange: (p: QuestParams) => void;
}) {
  const fieldStyleProps = {
    bg: "gray.900",
    borderColor: "gray.600",
    color: "white",
    _hover: { borderColor: "gray.500" },
    _focus: { borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" },
  };
  const inputProps = {
    ...fieldStyleProps,
    size: "sm" as const,
  };

  if (kind === "DAILY_TRADE_ASSET") {
    return (
      <FormControl>
        <FormLabel color="gray.400" fontSize="sm">
          Asset
        </FormLabel>
        <Input
          {...inputProps}
          value={params.asset ?? ""}
          placeholder="BTC / ETH / SOL / ARB"
          onChange={(e) => onChange({ ...params, asset: e.target.value })}
        />
      </FormControl>
    );
  }

  if (kind === "DAILY_TRADE_VOLUME") {
    return (
      <FormControl>
        <FormLabel color="gray.400" fontSize="sm">
          Min Volume (USD)
        </FormLabel>
        <NumberInput
          min={0}
          value={params.minVolumeUsd ?? 0}
          onChange={(_, v) => onChange({ ...params, minVolumeUsd: isNaN(v) ? 0 : v })}
          size="sm"
        >
          <NumberInputField {...fieldStyleProps} />
          <NumberInputStepper>
            <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
            <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
          </NumberInputStepper>
        </NumberInput>
      </FormControl>
    );
  }

  if (kind === "DAILY_TRADE_COUNT" || kind === "DAILY_TRADE_BOTH_SIDES" || kind === "DAILY_TRADE_PROFIT") {
    return (
      <FormControl>
        <FormLabel color="gray.400" fontSize="sm">
          Trade Count
        </FormLabel>
        <NumberInput
          min={1}
          value={params.tradeCount ?? 1}
          onChange={(_, v) => onChange({ ...params, tradeCount: isNaN(v) ? 1 : v })}
          size="sm"
        >
          <NumberInputField {...fieldStyleProps} />
          <NumberInputStepper>
            <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
            <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
          </NumberInputStepper>
        </NumberInput>
      </FormControl>
    );
  }

  if (kind === "DAILY_TRADE_SPEED") {
    return (
      <VStack spacing={3} align="stretch">
        <FormControl>
          <FormLabel color="gray.400" fontSize="sm">
            Trade Count
          </FormLabel>
          <NumberInput
            min={1}
            value={params.tradeCount ?? 1}
            onChange={(_, v) => onChange({ ...params, tradeCount: isNaN(v) ? 1 : v })}
            size="sm"
          >
            <NumberInputField {...fieldStyleProps} />
            <NumberInputStepper>
              <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
              <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel color="gray.400" fontSize="sm">
            Window (minutes)
          </FormLabel>
          <NumberInput
            min={1}
            value={params.windowMinutes ?? 60}
            onChange={(_, v) => onChange({ ...params, windowMinutes: isNaN(v) ? 60 : v })}
            size="sm"
          >
            <NumberInputField {...fieldStyleProps} />
            <NumberInputStepper>
              <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
              <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>
      </VStack>
    );
  }

  if (kind === "DAILY_TRADE_HIGH_STAKES") {
    return (
      <FormControl>
        <FormLabel color="gray.400" fontSize="sm">
          Min Single Trade (USD)
        </FormLabel>
        <NumberInput
          min={0}
          value={params.minSingleTradeUsd ?? 0}
          onChange={(_, v) => onChange({ ...params, minSingleTradeUsd: isNaN(v) ? 0 : v })}
          size="sm"
        >
          <NumberInputField {...fieldStyleProps} />
          <NumberInputStepper>
            <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
            <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
          </NumberInputStepper>
        </NumberInput>
      </FormControl>
    );
  }

  if (kind === "DAILY_TRADE_DIVERSITY") {
    return (
      <FormControl>
        <FormLabel color="gray.400" fontSize="sm">
          Diversity Count (# distinct assets)
        </FormLabel>
        <NumberInput
          min={1}
          value={params.diversityCount ?? 2}
          onChange={(_, v) => onChange({ ...params, diversityCount: isNaN(v) ? 2 : v })}
          size="sm"
        >
          <NumberInputField {...fieldStyleProps} />
          <NumberInputStepper>
            <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
            <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
          </NumberInputStepper>
        </NumberInput>
      </FormControl>
    );
  }

  if (kind === "DAILY_COMMENT_TWITTER") {
    return (
      <FormControl>
        <FormLabel color="gray.400" fontSize="sm">
          Tweet URL
        </FormLabel>
        <Input
          {...inputProps}
          value={params.tweetUrl ?? ""}
          placeholder="https://twitter.com/..."
          onChange={(e) => onChange({ ...params, tweetUrl: e.target.value })}
        />
      </FormControl>
    );
  }

  return null;
}

// ─── Edit / Create Modal ───────────────────────────────────────────────────────

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  initial: DailyTemplate | null; // null = create mode
  onSaved: (t: DailyTemplate) => void;
}

function TemplateModal({ isOpen, onClose, initial, onSaved }: TemplateModalProps) {
  const isEdit = initial !== null;
  const [form, setForm] = useState<DailyTemplate>(initial ?? BLANK_TEMPLATE);
  const [saving, setSaving] = useState(false);

  // Sync form when modal opens with a different template
  useEffect(() => {
    setForm(initial ?? BLANK_TEMPLATE);
  }, [initial, isOpen]);

  const set = <K extends keyof DailyTemplate>(k: K, v: DailyTemplate[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const fieldStyleProps = {
    bg: "gray.900",
    borderColor: "gray.600",
    color: "white",
    _hover: { borderColor: "gray.500" },
    _focus: { borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" },
  };
  const inputProps = {
    ...fieldStyleProps,
    size: "sm" as const,
  };

  const save = async () => {
    if (!form.templateId.trim()) {
      toast.error("Template ID is required");
      return;
    }
    if (!form.titleTemplate.trim()) {
      toast.error("Title Template is required");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/api/arena/daily-templates", form);
      const saved: DailyTemplate = res.data.data ?? res.data;
      toast.success(isEdit ? "Template updated!" : "Template created!");
      onSaved(saved);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="gray.800" borderColor="gray.700" border="1px solid">
        <ModalHeader color="white" borderBottom="1px solid" borderColor="gray.700">
          {isEdit ? "Edit Template" : "New Template"}
        </ModalHeader>
        <ModalCloseButton color="gray.400" />
        <ModalBody py={5}>
          <VStack spacing={4} align="stretch">
            {/* Template ID */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Template ID
              </FormLabel>
              <Input
                {...inputProps}
                value={form.templateId}
                isReadOnly={isEdit}
                onChange={(e) => set("templateId", e.target.value)}
                placeholder="e.g. daily_trade_btc_v1"
                _readOnly={{ opacity: 0.6, cursor: "not-allowed" }}
              />
              {isEdit && (
                <FormHelperText color="gray.500" fontSize="xs">
                  Template ID cannot be changed after creation.
                </FormHelperText>
              )}
            </FormControl>

            {/* Kind */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Kind
              </FormLabel>
              <Select
                value={form.kind}
                onChange={(e) => {
                  set("kind", e.target.value as QuestKind);
                  set("params", {}); // reset params on kind change
                }}
                bg="gray.900"
                borderColor="gray.600"
                color="white"
                size="sm"
                _hover={{ borderColor: "gray.500" }}
                _focus={{ borderColor: "brand.500" }}
              >
                {QUEST_KINDS.map((k) => (
                  <option key={k} value={k} style={{ background: "#1a202c" }}>
                    {k}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Title Template */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Title Template
              </FormLabel>
              <Input
                {...inputProps}
                value={form.titleTemplate}
                onChange={(e) => set("titleTemplate", e.target.value)}
                placeholder="e.g. Trade {asset} today"
              />
              <FormHelperText color="gray.500" fontSize="xs">
                Available tokens: {"{asset}"}, {"{amount}"}, {"{count}"}, {"{singleAmount}"},{" "}
                {"{diversity}"}, {"{window}"}
              </FormHelperText>
            </FormControl>

            {/* Description Template */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Description Template
              </FormLabel>
              <Textarea
                value={form.descriptionTemplate}
                onChange={(e) => set("descriptionTemplate", e.target.value)}
                placeholder="e.g. Place at least 1 trade on {asset} today to earn XP."
                bg="gray.900"
                borderColor="gray.600"
                color="white"
                size="sm"
                rows={2}
                _hover={{ borderColor: "gray.500" }}
                _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
              />
            </FormControl>

            <Divider borderColor="gray.700" />

            {/* Reward row */}
            <HStack spacing={4} align="flex-start">
              <FormControl>
                <FormLabel color="gray.400" fontSize="sm">
                  Reward XP
                </FormLabel>
                <NumberInput
                  min={0}
                  value={form.rewardXp}
                  onChange={(_, v) => set("rewardXp", isNaN(v) ? 0 : v)}
                  size="sm"
                >
                  <NumberInputField {...fieldStyleProps} />
                  <NumberInputStepper>
                    <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
                    <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel color="gray.400" fontSize="sm">
                  Reward Tickets
                </FormLabel>
                <NumberInput
                  min={0}
                  value={form.rewardTickets}
                  onChange={(_, v) => set("rewardTickets", isNaN(v) ? 0 : v)}
                  size="sm"
                >
                  <NumberInputField {...fieldStyleProps} />
                  <NumberInputStepper>
                    <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
                    <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel color="gray.400" fontSize="sm">
                  Weight (1–10)
                </FormLabel>
                <NumberInput
                  min={1}
                  max={10}
                  value={form.weight}
                  onChange={(_, v) => set("weight", isNaN(v) ? 1 : Math.min(10, Math.max(1, v)))}
                  size="sm"
                >
                  <NumberInputField {...fieldStyleProps} />
                  <NumberInputStepper>
                    <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
                    <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </HStack>

            <Divider borderColor="gray.700" />

            {/* Kind-specific params */}
            <Box>
              <Text color="gray.400" fontSize="sm" fontWeight="600" mb={3} textTransform="uppercase" letterSpacing="wider">
                Kind Parameters
              </Text>
              <ParamFields
                kind={form.kind}
                params={form.params}
                onChange={(p) => set("params", p)}
              />
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor="gray.700" gap={2}>
          <Button variant="ghost" color="gray.400" onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button colorScheme="brand" onClick={save} isLoading={saving} loadingText="Saving…" size="sm">
            {isEdit ? "Update Template" : "Create Template"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function DailyQuests() {
  // Config state
  const [config, setConfig] = useState<DailyConfig>({ activePerDay: 3, enabled: true });
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState<DailyTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  // Action states
  const [rotating, setRotating] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Edit/create modal
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const [editTarget, setEditTarget] = useState<DailyTemplate | null>(null); // null = create

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<DailyTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null!);

  // ── Load config ──────────────────────────────────────────────────────────────
  useEffect(() => {
    api
      .get("/api/arena/daily-config")
      .then((res) => {
        const d = res.data.data ?? res.data;
        setConfig({ activePerDay: d.activePerDay ?? 3, enabled: d.enabled ?? true });
      })
      .catch(() => toast.error("Failed to load daily quest config"))
      .finally(() => setConfigLoading(false));
  }, []);

  // ── Load templates ───────────────────────────────────────────────────────────
  const loadTemplates = () => {
    setTemplatesLoading(true);
    api
      .get("/api/arena/daily-templates")
      .then((res) => {
        const list: DailyTemplate[] = res.data.data ?? res.data ?? [];
        setTemplates(list);
      })
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setTemplatesLoading(false));
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // ── Save config ──────────────────────────────────────────────────────────────
  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      const res = await api.put("/api/arena/daily-config", config);
      const d = res.data.data ?? res.data;
      setConfig({ activePerDay: d.activePerDay ?? config.activePerDay, enabled: d.enabled ?? config.enabled });
      toast.success("Daily quest config saved!");
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to save config");
    } finally {
      setConfigSaving(false);
    }
  };

  // ── Rotate ───────────────────────────────────────────────────────────────────
  const rotateNow = async () => {
    setRotating(true);
    try {
      const res = await api.post("/api/arena/daily-rotate");
      const quests: Array<{ title?: string; titleTemplate?: string }> = res.data.data ?? res.data ?? [];
      const names = quests
        .map((q) => q.title ?? q.titleTemplate ?? "Quest")
        .slice(0, 5)
        .join(", ");
      toast.success(
        quests.length
          ? `Rotated! Today's quests (${quests.length}): ${names}`
          : "Rotated — no quests selected (check config/templates).",
        { duration: 6000 }
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Rotate failed");
    } finally {
      setRotating(false);
    }
  };

  // ── Seed defaults ────────────────────────────────────────────────────────────
  const seedDefaults = async () => {
    setSeeding(true);
    try {
      await api.post("/api/arena/daily-templates/seed");
      toast.success("12 default templates seeded!");
      loadTemplates();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  // ── Inline toggle enabled ────────────────────────────────────────────────────
  const toggleEnabled = async (t: DailyTemplate) => {
    const updated = { ...t, enabled: !t.enabled };
    // Optimistic
    setTemplates((prev) => prev.map((x) => (x.templateId === t.templateId ? updated : x)));
    try {
      await api.post("/api/arena/daily-templates", updated);
    } catch (err: any) {
      // Revert
      setTemplates((prev) => prev.map((x) => (x.templateId === t.templateId ? t : x)));
      toast.error(err?.response?.data?.message ?? "Failed to update template");
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/arena/daily-templates/${deleteTarget.templateId}`);
      setTemplates((prev) => prev.filter((t) => t.templateId !== deleteTarget.templateId));
      toast.success("Template deleted");
      setDeleteTarget(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  // ── Open edit modal ──────────────────────────────────────────────────────────
  const openEdit = (t: DailyTemplate) => {
    setEditTarget(t);
    onModalOpen();
  };

  const openCreate = () => {
    setEditTarget(null);
    onModalOpen();
  };

  // ── On saved callback ────────────────────────────────────────────────────────
  const onSaved = (saved: DailyTemplate) => {
    setTemplates((prev) => {
      const idx = prev.findIndex((t) => t.templateId === saved.templateId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
  };

  if (configLoading && templatesLoading) return <PageLoader />;

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="white">
            Daily Quests
          </Heading>
          <Text color="gray.400" mt={1} fontSize="sm">
            Manage daily quest templates and rotation config
          </Text>
        </Box>
        <HStack>
          <Badge
            colorScheme={config.enabled ? "green" : "red"}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
          >
            {config.enabled ? "Active" : "Disabled"}
          </Badge>
        </HStack>
      </Flex>

      <VStack spacing={6} align="stretch">
        {/* ── Section 1: Config bar ─────────────────────────────────────────── */}
        <Box bg="gray.800" borderRadius="xl" border="1px solid" borderColor="gray.700" p={6}>
          <Heading size="sm" color="gray.300" mb={5} textTransform="uppercase" letterSpacing="wider">
            Configuration
          </Heading>

          <Flex gap={6} wrap="wrap" align="flex-end">
            {/* Active per day */}
            <FormControl maxW="180px">
              <FormLabel color="gray.400" fontSize="sm">
                Active Quests per Day
              </FormLabel>
              <NumberInput
                min={1}
                max={10}
                value={config.activePerDay}
                onChange={(_, v) =>
                  setConfig((p) => ({ ...p, activePerDay: isNaN(v) ? 1 : Math.min(10, Math.max(1, v)) }))
                }
                size="sm"
              >
                <NumberInputField
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _hover={{ borderColor: "gray.500" }}
                  _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
                  <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            {/* Enable/Disable switch */}
            <FormControl display="flex" alignItems="center" gap={3} maxW="200px" pb={1}>
              <Switch
                id="dq-enabled"
                isChecked={config.enabled}
                onChange={(e) => setConfig((p) => ({ ...p, enabled: e.target.checked }))}
                colorScheme="green"
                size="md"
              />
              <FormLabel htmlFor="dq-enabled" mb={0} color="white" cursor="pointer" fontSize="sm">
                Enabled
              </FormLabel>
            </FormControl>

            {/* Save config */}
            <Button
              colorScheme="brand"
              size="sm"
              onClick={saveConfig}
              isLoading={configSaving}
              loadingText="Saving…"
            >
              Save Config
            </Button>

            <Box flex={1} />

            {/* Rotate Now */}
            <Button
              colorScheme="purple"
              size="sm"
              onClick={rotateNow}
              isLoading={rotating}
              loadingText="Rotating…"
              variant="outline"
            >
              Rotate Now
            </Button>

            {/* Seed Defaults */}
            <Button
              colorScheme="orange"
              size="sm"
              onClick={seedDefaults}
              isLoading={seeding}
              loadingText="Seeding…"
              variant="outline"
            >
              Seed Defaults (12 templates)
            </Button>
          </Flex>
        </Box>

        {/* ── Section 2: Template table ─────────────────────────────────────── */}
        <Box bg="gray.800" borderRadius="xl" border="1px solid" borderColor="gray.700" p={6}>
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="sm" color="gray.300" textTransform="uppercase" letterSpacing="wider">
              Quest Templates ({templates.length})
            </Heading>
            <Button
              colorScheme="brand"
              size="sm"
              leftIcon={<AddIcon boxSize={3} />}
              onClick={openCreate}
            >
              New Template
            </Button>
          </Flex>

          {templatesLoading ? (
            <Flex justify="center" py={10}>
              <Text color="gray.500">Loading templates…</Text>
            </Flex>
          ) : templates.length === 0 ? (
            <Alert status="info" bg="gray.700" borderRadius="lg">
              <AlertIcon />
              <Text color="gray.300" fontSize="sm">
                No templates yet. Click "New Template" to create one, or "Seed Defaults" to load 12
                starter templates.
              </Text>
            </Alert>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th color="gray.400" borderColor="gray.700">
                      Kind
                    </Th>
                    <Th color="gray.400" borderColor="gray.700">
                      Title Template
                    </Th>
                    <Th color="gray.400" borderColor="gray.700" isNumeric>
                      XP
                    </Th>
                    <Th color="gray.400" borderColor="gray.700" isNumeric>
                      Tickets
                    </Th>
                    <Th color="gray.400" borderColor="gray.700" isNumeric>
                      Weight
                    </Th>
                    <Th color="gray.400" borderColor="gray.700" textAlign="center">
                      Enabled
                    </Th>
                    <Th color="gray.400" borderColor="gray.700" />
                  </Tr>
                </Thead>
                <Tbody>
                  {templates.map((t) => (
                    <Tr key={t.templateId} _hover={{ bg: "gray.750" }}>
                      <Td borderColor="gray.700" py={2.5}>
                        <Badge
                          colorScheme="blue"
                          fontSize="xs"
                          px={2}
                          py={0.5}
                          borderRadius="md"
                          fontFamily="mono"
                        >
                          {t.kind.replace("DAILY_", "")}
                        </Badge>
                      </Td>
                      <Td borderColor="gray.700" py={2.5} maxW="260px">
                        <Text color="white" fontSize="sm" noOfLines={1}>
                          {t.titleTemplate}
                        </Text>
                        <Text color="gray.500" fontSize="xs" fontFamily="mono">
                          {t.templateId}
                        </Text>
                      </Td>
                      <Td borderColor="gray.700" py={2.5} isNumeric>
                        <Text color="green.300" fontSize="sm" fontWeight="600">
                          {t.rewardXp}
                        </Text>
                      </Td>
                      <Td borderColor="gray.700" py={2.5} isNumeric>
                        <Text color="yellow.300" fontSize="sm" fontWeight="600">
                          {t.rewardTickets}
                        </Text>
                      </Td>
                      <Td borderColor="gray.700" py={2.5} isNumeric>
                        <Text color="gray.300" fontSize="sm">
                          {t.weight}
                        </Text>
                      </Td>
                      <Td borderColor="gray.700" py={2.5} textAlign="center">
                        <Switch
                          isChecked={t.enabled}
                          onChange={() => toggleEnabled(t)}
                          colorScheme="green"
                          size="sm"
                        />
                      </Td>
                      <Td borderColor="gray.700" py={2.5}>
                        <HStack spacing={1} justify="flex-end">
                          <IconButton
                            aria-label="Edit template"
                            icon={<EditIcon />}
                            size="xs"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => openEdit(t)}
                          />
                          <IconButton
                            aria-label="Delete template"
                            icon={<DeleteIcon />}
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => setDeleteTarget(t)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>

        {/* ── Today's active quests note ────────────────────────────────────── */}
        <Box bg="gray.800" borderRadius="xl" border="1px solid" borderColor="gray.700" p={6}>
          <Heading size="sm" color="gray.300" mb={3} textTransform="uppercase" letterSpacing="wider">
            Today's Active Quests
          </Heading>
          <Alert status="info" bg="gray.700" borderRadius="lg">
            <AlertIcon />
            <Text color="gray.300" fontSize="sm">
              Today's active quests are selected at midnight UTC from the enabled templates using
              weighted random selection. Use{" "}
              <Text as="span" color="purple.300" fontWeight="600">
                "Rotate Now"
              </Text>{" "}
              above to preview or force today's selection. The toast will list the selected quest
              titles.
            </Text>
          </Alert>
        </Box>
      </VStack>

      {/* ── Edit / Create Modal ─────────────────────────────────────────────── */}
      <TemplateModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        initial={editTarget}
        onSaved={onSaved}
      />

      {/* ── Delete Confirmation Dialog ──────────────────────────────────────── */}
      <AlertDialog
        isOpen={deleteTarget !== null}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteTarget(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" borderColor="gray.700" border="1px solid">
            <AlertDialogHeader color="white">Delete Template</AlertDialogHeader>
            <AlertDialogBody color="gray.300">
              Delete template{" "}
              <Text as="span" fontWeight="bold" color="white" fontFamily="mono">
                {deleteTarget?.templateId}
              </Text>
              ? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button
                ref={cancelRef}
                variant="ghost"
                color="gray.400"
                onClick={() => setDeleteTarget(null)}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                size="sm"
                onClick={confirmDelete}
                isLoading={deleting}
                loadingText="Deleting…"
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
}
